import React, { useState, useEffect } from 'react';
import './TradeHistory.css';

const TradeHistory = ({ allTrades = [], totalProfit = 0, dailyProfit = 0 }) => {
  const [filteredTrades, setFilteredTrades] = useState([]);
  const [filters, setFilters] = useState({
    type: 'all',
    status: 'all',
    pair: 'all',
    dateRange: 'all'
  });
  const [sortBy, setSortBy] = useState('timestamp');
  const [sortOrder, setSortOrder] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');

  // Process trades from props
  const processedTrades = allTrades.map(trade => {
    // Calculate duration if not present
    const duration = trade.duration || 'N/A';
    
    // Calculate fees if not present (estimate 0.1% of trade amount)
    const fees = trade.fees || (trade.amount * 0.001);
    
    // Ensure we have all required fields
    return {
      id: trade.id,
      pair: trade.pair || 'N/A',
      type: trade.type || 'MANUAL',
      action: trade.action || 'BUY',
      amount: trade.amount || 0,
      leverage: trade.leverage || 1,
      entryPrice: trade.entryPrice || trade.price || 0,
      exitPrice: trade.exitPrice || trade.currentPrice || trade.entryPrice || 0,
      profitLoss: trade.profitLoss || 0,
      profitPercent: trade.profitPercent || 0,
      status: trade.status || 'ACTIVE',
      timestamp: new Date(trade.timestamp),
      duration: duration,
      fees: fees
    };
  });

  // Initialize with processed trades
  useEffect(() => {
    setFilteredTrades(processedTrades);
  }, [allTrades]);

  // Filter and sort trades
  useEffect(() => {
    let filtered = [...processedTrades];

    // Apply filters
    if (filters.type !== 'all') {
      filtered = filtered.filter(trade => trade.type === filters.type);
    }
    if (filters.status !== 'all') {
      filtered = filtered.filter(trade => trade.status === filters.status);
    }
    if (filters.pair !== 'all') {
      filtered = filtered.filter(trade => trade.pair === filters.pair);
    }
    if (filters.dateRange !== 'all') {
      const now = new Date();
      const days = filters.dateRange === '7d' ? 7 : filters.dateRange === '30d' ? 30 : 90;
      const cutoff = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
      filtered = filtered.filter(trade => trade.timestamp >= cutoff);
    }

    // Apply search
    if (searchTerm) {
      filtered = filtered.filter(trade => 
        trade.pair.toLowerCase().includes(searchTerm.toLowerCase()) ||
        trade.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
        trade.action.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'timestamp':
          aValue = a.timestamp.getTime();
          bValue = b.timestamp.getTime();
          break;
        case 'profitLoss':
          aValue = a.profitLoss;
          bValue = b.profitLoss;
          break;
        case 'profitPercent':
          aValue = a.profitPercent;
          bValue = b.profitPercent;
          break;
        case 'amount':
          aValue = a.amount;
          bValue = b.amount;
          break;
        default:
          aValue = a.timestamp.getTime();
          bValue = b.timestamp.getTime();
      }

      return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
    });

    setFilteredTrades(filtered);
  }, [processedTrades, filters, sortBy, sortOrder, searchTerm]);

  const getTypeIcon = (type) => {
    switch (type) {
      case 'MANUAL': return '👤';
      case 'AUTOMATIC': return '🤖';
      case 'ALERT': return '📡';
      case 'CONTRACT_TRADING': return '📊';
      case 'ARBITRAGE': return '🔄';
      case 'OPTIONS': return '📈';
      default: return '❓';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'CLOSED': return '#4caf50';
      case 'ACTIVE': return '#ff9800';
      case 'CANCELLED': return '#f44336';
      default: return '#9e9e9e';
    }
  };

  const getProfitColor = (profit) => {
    return profit >= 0 ? '#4caf50' : '#f44336';
  };

  const displayTotalProfit = totalProfit || filteredTrades.reduce((sum, trade) => sum + trade.profitLoss, 0);
  const displayDailyProfit = dailyProfit || 0;
  const totalTrades = filteredTrades.length;
  const winRate = totalTrades > 0 ? (filteredTrades.filter(trade => trade.profitLoss > 0).length / totalTrades * 100) : 0;
  const avgProfit = totalTrades > 0 ? (displayTotalProfit / totalTrades) : 0;

  return (
    <div className="trade-history-page">
      {/* Header */}
      <div className="history-header">
        <h2>Trade History</h2>
        <p>Complete trading history and performance analytics</p>
      </div>

      {/* Summary Stats */}
      <div className="summary-stats">
        <div className="stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-content">
            <div className="stat-value">{totalTrades}</div>
            <div className="stat-label">Total Trades</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">💰</div>
          <div className="stat-content">
            <div className={`stat-value ${displayTotalProfit >= 0 ? 'positive' : 'negative'}`}>
              ${displayTotalProfit.toFixed(2)}
            </div>
            <div className="stat-label">Total P&L</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">📅</div>
          <div className="stat-content">
            <div className={`stat-value ${displayDailyProfit >= 0 ? 'positive' : 'negative'}`}>
              ${displayDailyProfit.toFixed(2)}
            </div>
            <div className="stat-label">Daily P&L</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">🎯</div>
          <div className="stat-content">
            <div className="stat-value">{winRate.toFixed(1)}%</div>
            <div className="stat-label">Win Rate</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">📈</div>
          <div className="stat-content">
            <div className={`stat-value ${avgProfit >= 0 ? 'positive' : 'negative'}`}>
              ${avgProfit.toFixed(2)}
            </div>
            <div className="stat-label">Avg P&L</div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="filters-section">
        <div className="search-bar">
          <input
            type="text"
            placeholder="Search trades..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filters-grid">
          <div className="filter-group">
            <label>Type</label>
            <select
              value={filters.type}
              onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
            >
              <option value="all">All Types</option>
              <option value="MANUAL">Manual</option>
              <option value="AUTOMATIC">Automatic</option>
              <option value="ALERT">Alert</option>
              <option value="CONTRACT_TRADING">Contract Trading</option>
              <option value="ARBITRAGE">Arbitrage</option>
              <option value="OPTIONS">Options</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
            >
              <option value="all">All Status</option>
              <option value="CLOSED">Closed</option>
              <option value="ACTIVE">Active</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Pair</label>
            <select
              value={filters.pair}
              onChange={(e) => setFilters(prev => ({ ...prev, pair: e.target.value }))}
            >
              <option value="all">All Pairs</option>
              <option value="BTC">BTC</option>
              <option value="ETH">ETH</option>
              <option value="SOL">SOL</option>
              <option value="ADA">ADA</option>
              <option value="DOT">DOT</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Date Range</label>
            <select
              value={filters.dateRange}
              onChange={(e) => setFilters(prev => ({ ...prev, dateRange: e.target.value }))}
            >
              <option value="all">All Time</option>
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
              <option value="90d">Last 90 Days</option>
            </select>
          </div>
        </div>

        <div className="sort-controls">
          <div className="sort-group">
            <label>Sort By</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="timestamp">Date</option>
              <option value="profitLoss">P&L</option>
              <option value="profitPercent">Profit %</option>
              <option value="amount">Amount</option>
            </select>
          </div>
          <div className="sort-group">
            <label>Order</label>
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
            >
              <option value="desc">Descending</option>
              <option value="asc">Ascending</option>
            </select>
          </div>
        </div>
      </div>

      {/* Trades Table */}
      <div className="trades-table-container">
        <div className="trades-table">
          <div className="table-header">
            <div className="header-cell type">Type</div>
            <div className="header-cell pair">Pair</div>
            <div className="header-cell action">Action</div>
            <div className="header-cell amount">Amount</div>
            <div className="header-cell leverage">Leverage</div>
            <div className="header-cell entry">Entry Price</div>
            <div className="header-cell exit">Exit Price</div>
            <div className="header-cell pnl">P&L</div>
            <div className="header-cell percent">Profit %</div>
            <div className="header-cell duration">Duration</div>
            <div className="header-cell fees">Fees</div>
            <div className="header-cell status">Status</div>
            <div className="header-cell date">Date</div>
          </div>

          <div className="table-body">
            {filteredTrades.length === 0 ? (
              <div className="no-trades">
                <div className="no-trades-icon">📊</div>
                <p>No trades found matching your criteria</p>
              </div>
            ) : (
              filteredTrades.map(trade => (
                <div key={trade.id} className="table-row">
                  <div className="table-cell type">
                    <span className="type-icon">{getTypeIcon(trade.type)}</span>
                    <span className="type-text">{trade.type}</span>
                  </div>
                  <div className="table-cell pair">{trade.pair}</div>
                  <div className="table-cell action">
                    <span className={`action-badge ${trade.action.toLowerCase()}`}>
                      {trade.action}
                    </span>
                  </div>
                  <div className="table-cell amount">${trade.amount.toLocaleString()}</div>
                  <div className="table-cell leverage">{trade.leverage}x</div>
                  <div className="table-cell entry">${trade.entryPrice.toLocaleString()}</div>
                  <div className="table-cell exit">${trade.exitPrice.toLocaleString()}</div>
                  <div className="table-cell pnl">
                    <span 
                      className="pnl-value"
                      style={{ color: getProfitColor(trade.profitLoss) }}
                    >
                      {trade.profitLoss >= 0 ? '+' : ''}${trade.profitLoss.toFixed(2)}
                    </span>
                  </div>
                  <div className="table-cell percent">
                    <span 
                      className="percent-value"
                      style={{ color: getProfitColor(trade.profitPercent) }}
                    >
                      {trade.profitPercent >= 0 ? '+' : ''}{trade.profitPercent.toFixed(2)}%
                    </span>
                  </div>
                  <div className="table-cell duration">{trade.duration}</div>
                  <div className="table-cell fees">${trade.fees.toFixed(2)}</div>
                  <div className="table-cell status">
                    <span 
                      className="status-badge"
                      style={{ backgroundColor: getStatusColor(trade.status) }}
                    >
                      {trade.status}
                    </span>
                  </div>
                  <div className="table-cell date">
                    {trade.timestamp.toLocaleDateString()}
                    <br />
                    <small>{trade.timestamp.toLocaleTimeString()}</small>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Export Options */}
      <div className="export-section">
        <h3>Export Data</h3>
        <div className="export-buttons">
          <button className="export-btn csv">Export as CSV</button>
          <button className="export-btn excel">Export as Excel</button>
          <button className="export-btn pdf">Export as PDF</button>
        </div>
      </div>
    </div>
  );
};

export default TradeHistory;
